import React, {
  FC, useEffect, useRef, useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Mixpanel from 'mixpanel-browser';
import QuizHeader from '~/components/QuizHeader';
import { satisfactionOptions, yesNoOptions } from '~/core/constants/questions';
import Options from '~/components/Options';
import { OptionProps } from '~/core/dto/components/optionsDTO';
import Button from '~/components/Button';
import Quiz from '~/core/helpers/quiz';
import { createNavigationPath } from '~/core/helpers/navigation';
import { NavigationPath, ScreenProps } from '~/core/dto/navigationDTO';

import './QuizQuestion.styles.css';
import InfoBox from '~/components/InfoBox';
import Spacer from '~/components/Spacer';

const QuizQuestion: FC<ScreenProps> = ( { type } ) => {

  const { id } = useParams();
  const navigate = useNavigate();
  const routeName = `QuizQuestion-${type}`;

  const questions = Quiz.getQuestions( type );
  const question = questions.find( ( q ) => q.id === id );
  const questionIndex = questions.findIndex( ( q ) => q.id === id );

  const [ answer, setAnswer ] = useState<string>();

  const messagesEndRef = useRef<HTMLDivElement>( null );

  if ( !question ) {

    return null;

  }

  const onAnswer = ( answerId: string ) => {

    Quiz.saveAnswer( question.id, answerId, type );

    if ( question.type === 'options' && question.didYouKnow && !answer ) {

      setAnswer( answerId );

      return;

    }

    setAnswer( undefined );
    const nextId = Quiz.getNextQuestion( type )?.id;

    setTimeout( () => {

      Mixpanel.track( routeName, { state: 1, questionId: id, answerId } );

      if ( nextId ) {

        navigate( createNavigationPath( type === 'adult' ? NavigationPath.AdultQuizQuestion : NavigationPath.QuizQuestion, nextId ), { replace: true } );

      } else {

        navigate( type === 'adult' ? NavigationPath.AdultFinancialIQ : NavigationPath.PersonalGrowth, { replace: true } );

      }

    }, answer ? 0 : 500 );

  };

  useEffect( () => {

    if ( answer ) {

      messagesEndRef.current?.scrollIntoView( { behavior: 'smooth' } );

    }

  }, [ answer ] );

  useEffect( () => {

    Mixpanel.track( routeName, { state: 0, questionId: id } );

  }, [] );

  const width: OptionProps['width'] = question.type === 'imageOptions' ? '40%' : undefined;

  return (
    <div className="container">
      <QuizHeader type={type} progress={( questionIndex + 1 ) / ( questions.length + 1 )} />
      <div className="scrollContainer">
        <Spacer size={24} />
        {question?.type === 'options' && question.isKnowledge && (
          <>
            <h3>LET’S TEST YOUR KNOWLEDGE!</h3>
            <Spacer size={8} />
          </>
        )}
        <h1 className="textCenter">{question?.title}</h1>
        {question?.type === 'options' && question?.subtitle && (
          <>
            <Spacer size={8} />
            <p className="textCenter">{question?.subtitle}</p>
          </>
        )}
        <Spacer size={24} />
        <div className={`questionContainer flexColumn${![ 'options', 'multipleOptions' ].includes( question.type ) ? ' justifyCenter' : ''}`}>
          {question?.type === 'options' && question.didYouKnow && answer && <InfoBox image={question.didYouKnow.icon} description={question.didYouKnow.description} title="Did you know?" />}
          {( question.type === 'options' || question.type === 'imageOptions' ) && (
          <Options
            callback={onAnswer}
            gap={question.type === 'imageOptions' ? 16 : 8}
            options={question.options.map( ( item ) => ( {
              ...item, isImage: question.type === 'imageOptions', width,
            } ) )}
            dataChanged={id}
          />
          )}
          {question.type === 'multipleOptions' && (
            <div className="multipleOptions">
              <Options
                callback={setAnswer}
                gap={8}
                options={question.options}
                dataChanged={id}
                multiple
              />
            </div>
          )}
          {question.type === 'satisfaction' && (
            <div>
              <Options
                callback={onAnswer}
                options={satisfactionOptions}
                dataChanged={id}
              />
              <Spacer size={8} />
              <div className="flexRow justifySpaceBetween alignCenter">
                <p>Not really</p>
                <p>Yes, sure</p>
              </div>
            </div>
          )}
          {question.type === 'yesNo' && (
            <div>
              <Options
                gap={20}
                callback={onAnswer}
                options={yesNoOptions}
                dataChanged={id}
              />
              <Spacer size={8} />
              <div className="yesNoDescription flexRow justifyCenter alignCenter">
                <p className="textCenter">Not really</p>
                <p className="textCenter">Yes, sure</p>
              </div>
            </div>
          )}
          {[ 'options', 'multipleOptions' ].includes( question.type ) && (
          <div className="questionFooter flexColumn justifyCenter alignCenter">
            {( answer || question.type === 'multipleOptions' ) && <Button disabled={!answer} onPress={() => onAnswer( answer! )}>Continue</Button>}
          </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  );

};

export default QuizQuestion;
